import { useCallback, useRef, useState } from "react";
import { load } from "@2gis/mapgl";
import { getMapStyle } from "../../utils/map/mapStyles";
import { MapInstance, MapGL } from "@/types/map";

export const useWarmMap = () => {
  const [isWarmMapReady, setIsWarmMapReady] = useState(false);
  const warmMapRef = useRef<HTMLDivElement | null>(null);
  const warmMapInstanceRef = useRef<MapInstance | null>(null);
  const mapglRef = useRef<MapGL | null>(null);
  const isInitializingRef = useRef(false);

  const initWarmMap = useCallback(async (
    selectedCity: any,
    mapToken: string,
    theme: "light" | "dark"
  ) => {
    if (!selectedCity || !warmMapRef.current || isInitializingRef.current || warmMapInstanceRef.current) {
      return;
    }

    isInitializingRef.current = true;

    try {
      console.log("🔥 Инициализация warm map для LocationClarification");

      if (!mapglRef.current) {
        const loadedMapgl = await load();
        mapglRef.current = loadedMapgl as unknown as MapGL;
      }

      const mapStyle = getMapStyle(theme);

      warmMapInstanceRef.current = new mapglRef.current.Map(warmMapRef.current, {
        center: [selectedCity.longitude, selectedCity.latitude],
        zoom: 16,
        key: mapToken,
        rotationControl: false,
        zoomControl: false,
        trafficControl: false,
        copyright: false,
        animate: false,
        style: mapStyle,
      });

      warmMapInstanceRef.current.once("styleload", () => {
        console.log("✅ Warm map готова");
        setIsWarmMapReady(true);
      });
    } catch (err) {
      console.error("❌ Ошибка при инициализации warm map:", err);
    } finally {
      isInitializingRef.current = false;
    }
  }, []);

  const moveWarmMapToContainer = useCallback((targetContainer: HTMLDivElement) => {
    if (warmMapRef.current && warmMapInstanceRef.current) {
      console.log("🔄 Перенос warm map в модалку");

      targetContainer.appendChild(warmMapRef.current);

      setTimeout(() => {
        warmMapInstanceRef.current?.invalidateSize();
      }, 50);
    }
  }, []);

  const resetWarmMap = useCallback(() => {
    if (warmMapRef.current && warmMapInstanceRef.current) {
      console.log("🔄 Возврат warm map в offscreen");

      const offscreenContainer = document.getElementById("warm-map-offscreen");
      if (offscreenContainer) {
        offscreenContainer.appendChild(warmMapRef.current);
      }
    }
  }, []);

  const destroyWarmMap = useCallback(() => {
    if (warmMapInstanceRef.current) {
      console.log("🧹 Очистка warm map");
      setIsWarmMapReady(false);
      warmMapInstanceRef.current.destroy();
      warmMapInstanceRef.current = null;
      isInitializingRef.current = false;
    }
  }, []);

  return {
    warmMapRef,
    isWarmMapReady,
    warmMapInstance: warmMapInstanceRef.current,
    initWarmMap,
    moveWarmMapToContainer,
    resetWarmMap,
    destroyWarmMap,
  };
};
